import React, { useState, useMemo, useRef, useEffect } from 'react';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const DropdownSelect = ({ label, options, value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((o) =>
      o.name.toLowerCase().includes(q) ||
      (o.venue_name && o.venue_name.toLowerCase().includes(q))
    );
  }, [options, search]);

  const selectedLabel = value
    ? (options.find((o) => o.id === value)?.name || 'Selected')
    : null;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => { setOpen(!open); setSearch(''); }}
        style={{
          fontSize: 13,
          fontWeight: 500,
          padding: '4px 12px',
          borderRadius: 6,
          border: value ? '1px solid var(--sl-fg-brand, #1982c4)' : '1px solid var(--sl-border-primary, #d0d5dd)',
          background: value ? 'var(--sl-bg-brand-primary, #f6fafe)' : 'var(--sl-bg-primary, #fff)',
          color: value ? 'var(--sl-fg-brand, #1982c4)' : 'var(--sl-text-tertiary, #667085)',
          height: 32,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          width: 240,
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ fontSize: 11, color: 'var(--sl-text-tertiary, #667085)', fontWeight: 400 }}>{label}:</span>
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 200,
        }}>
          {selectedLabel || placeholder}
        </span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: 4,
          background: 'var(--sl-bg-primary, #fff)',
          border: '1px solid var(--sl-border-primary, #d0d5dd)',
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(16, 24, 40, 0.1)',
          zIndex: 1000,
          width: 380,
          maxHeight: 400,
          display: 'flex',
          flexDirection: 'column',
        }}>
          <div style={{ padding: '8px 8px 4px' }}>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search events..."
              autoFocus
              style={{
                width: '100%',
                fontSize: 13,
                padding: '6px 10px',
                border: '1px solid var(--sl-border-primary, #d0d5dd)',
                borderRadius: 6,
                outline: 'none',
              }}
            />
          </div>
          <div style={{ overflowY: 'auto', maxHeight: 340, padding: '4px 0' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '12px 16px', color: 'var(--sl-text-tertiary, #667085)', fontSize: 13 }}>
                No events found
              </div>
            ) : (
              filtered.map((option) => {
                const isSelected = option.id === value;
                const subtitle = [formatDate(option.event_date), option.venue_name].filter(Boolean).join(' \u00B7 ');
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => { onChange(option.id); setOpen(false); }}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 16px 10px',
                      border: 'none',
                      borderBottom: '1px solid var(--sl-bg-tertiary, #f2f4f7)',
                      background: isSelected ? 'var(--sl-bg-brand-primary, #f6fafe)' : 'transparent',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--sl-bg-tertiary, #f2f4f7)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = isSelected ? 'var(--sl-bg-brand-primary, #f6fafe)' : 'transparent'; }}
                  >
                    <div style={{
                      fontSize: 13,
                      fontWeight: isSelected ? 600 : 500,
                      color: isSelected ? 'var(--sl-fg-brand, #1982c4)' : 'var(--sl-text-secondary, #344054)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {option.name}
                    </div>
                    {subtitle && (
                      <div style={{
                        fontSize: 11,
                        color: 'var(--sl-text-tertiary, #667085)',
                        marginTop: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {subtitle}
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>
          {value && (
            <div style={{ borderTop: '1px solid var(--sl-border-secondary, #e4e7ec)', padding: '6px 8px' }}>
              <button
                type="button"
                onClick={() => { onChange(null); setOpen(false); }}
                style={{
                  fontSize: 12,
                  color: 'var(--sl-text-tertiary, #667085)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 8px',
                }}
              >
                Clear selection
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ComparisonEventSelector = ({ eventOptions, eventA, eventB, onSelectA, onSelectB }) => {
  const optionsForA = useMemo(
    () => eventOptions.filter((e) => e.id !== eventB),
    [eventOptions, eventB]
  );

  const optionsForB = useMemo(
    () => eventOptions.filter((e) => e.id !== eventA),
    [eventOptions, eventA]
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <DropdownSelect
        label="Event A"
        options={optionsForA}
        value={eventA}
        onChange={onSelectA}
        placeholder="Select event..."
      />
      <span style={{ color: 'var(--sl-text-tertiary, #667085)', fontSize: 13, fontWeight: 500 }}>vs</span>
      <DropdownSelect
        label="Event B"
        options={optionsForB}
        value={eventB}
        onChange={onSelectB}
        placeholder="Select event..."
      />
    </div>
  );
};

export default ComparisonEventSelector;
